import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  PageContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/status-badge";
import { isoSeconds, durationLabel } from "@/lib/format";

interface Row {
  id: string;
  job_id: string | null;
  job_name: string | null;
  status: string;
  trigger: string;
  started_at: string | null;
  finished_at: string | null;
}

interface RunsResponse {
  runs: Row[];
  total: number;
  limit: number;
  offset: number;
}

interface JobRow {
  id: string;
  name: string;
}

const PER_PAGE_OPTIONS = [10, 20, 50, 100] as const;
const DEFAULT_PER_PAGE = 20;

const searchSchema = z.object({
  status: z.string().optional(),
  job: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  perPage: z.coerce
    .number()
    .int()
    .refine((n) => (PER_PAGE_OPTIONS as readonly number[]).includes(n), {
      message: "invalid perPage",
    })
    .optional(),
});

export const Route = createFileRoute("/_authed/$companySlug/runs/")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  validateSearch: searchSchema,
  component: CompanyRunsListPage,
});

function CompanyRunsListPage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const { status, job, page, perPage } = Route.useSearch();
  const nav = useNavigate({ from: "/$companySlug/runs/" });
  const statusFilter = status ?? "all";
  const jobFilter = job ?? "all";
  const pageNum = page ?? 1;
  const pageSize = perPage ?? DEFAULT_PER_PAGE;
  const offset = (pageNum - 1) * pageSize;

  // Workflow dropdown source. /api/tenants/:id already returns the
  // full jobs[] list; reuse it instead of adding a new endpoint.
  // Cached for the lifetime of the page — jobs change rarely.
  const jobsQ = useQuery({
    queryKey: ["tenant", company.id, "jobs-for-runs-filter"],
    queryFn: () =>
      api<{ jobs: JobRow[] }>(`/api/tenants/${encodeURIComponent(company.id)}`),
    staleTime: 60_000,
  });

  // Helper: keep current filters when navigating, override only what's passed.
  const navWith = (
    patch: Partial<{ status: string; job: string; page: number; perPage: number }>,
  ) => {
    const next: {
      status?: string;
      job?: string;
      page?: number;
      perPage?: number;
    } = {
      status: statusFilter === "all" ? undefined : statusFilter,
      job: jobFilter === "all" ? undefined : jobFilter,
      page: pageNum === 1 ? undefined : pageNum,
      perPage: pageSize === DEFAULT_PER_PAGE ? undefined : pageSize,
      ...patch,
    };
    // Empty filters / defaults drop out of the URL.
    if (next.status === "all") next.status = undefined;
    if (next.job === "all") next.job = undefined;
    if (next.page === 1) next.page = undefined;
    if (next.perPage === DEFAULT_PER_PAGE) next.perPage = undefined;
    nav({ to: "/$companySlug/runs", params: { companySlug }, search: next });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["runs", company.id, statusFilter, jobFilter, pageNum, pageSize],
    queryFn: () => {
      const q = new URLSearchParams({
        tenant: company.id,
        limit: String(pageSize),
        offset: String(offset),
      });
      if (statusFilter !== "all") q.set("status", statusFilter);
      if (jobFilter !== "all") q.set("job", jobFilter);
      return api<RunsResponse>(`/api/runs?${q}`);
    },
    refetchInterval: 5_000,
  });

  // Live "what's executing right now" view. Driven by the same
  // /api/runs endpoint with status=running. Polls fast (5 s) so a
  // run that finishes drops off the section quickly.
  const runningQ = useQuery({
    queryKey: ["runs", company.id, "currently-running"],
    queryFn: () =>
      api<{ runs: Row[] }>(
        `/api/runs?tenant=${encodeURIComponent(company.id)}&status=running&limit=50`,
      ),
    refetchInterval: 5_000,
  });

  const totalPages =
    data && data.total > 0 ? Math.ceil(data.total / pageSize) : 1;

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
        <h1 className="sr-only">Runs</h1>

        {/* Currently running — fast-poll snapshot of in-flight runs.
            Visible at the top so an operator can dive into a stuck
            sync without scrolling the long history below. */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Currently running</h2>
          <Card>
            <CardContent className="p-0">
              {!runningQ.data ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {runningQ.isLoading ? "Loading…" : "—"}
                </div>
              ) : runningQ.data.runs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Nothing is running right now.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b">
                      <th className="text-left p-3">Workflow</th>
                      <th className="text-left p-3">Trigger</th>
                      <th className="text-left p-3">Started</th>
                      <th className="text-left p-3">Elapsed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runningQ.data.runs.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                        onClick={() => nav({
                          to: "/$companySlug/runs/$runId",
                          params: { companySlug, runId: r.id },
                          state: (previous) => ({
                            ...previous,
                            dataflowListReturn: {
                              companySlug, section: "runs", itemId: r.id,
                              search: window.location.search,
                            },
                          }),
                        })}
                      >
                        <td className="p-3">
                          {r.job_name ?? <span className="text-muted-foreground">ad-hoc</span>}
                        </td>
                        <td className="p-3 text-muted-foreground">{r.trigger}</td>
                        <td className="p-3 text-muted-foreground">{isoSeconds(r.started_at)}</td>
                        <td className="p-3 text-muted-foreground">
                          {durationLabel(r.started_at, null)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">History</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Workflow</span>
            <Select
              value={jobFilter}
              onValueChange={(v) => navWith({ job: v, page: 1 })}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workflows</SelectItem>
                {(jobsQ.data?.jobs ?? []).map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span className="text-sm text-muted-foreground">Status</span>
            <Select
              value={statusFilter}
              onValueChange={(v) => navWith({ status: v, page: 1 })}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {error ? (
          <Card><CardContent className="p-6 text-destructive">Failed to load runs.</CardContent></Card>
        ) : (
          <>
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b">
                      <th className="text-left p-3">Workflow</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Trigger</th>
                      <th className="text-left p-3">Started</th>
                      <th className="text-left p-3">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading || !data ? (
                      <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                    ) : data.runs.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No runs.</td></tr>
                    ) : (
                      data.runs.map((r) => (
                        <tr
                          key={r.id}
                          className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                          onClick={() => nav({
                            to: "/$companySlug/runs/$runId",
                            params: { companySlug, runId: r.id },
                            state: (previous) => ({
                              ...previous,
                              dataflowListReturn: {
                                companySlug, section: "runs", itemId: r.id,
                                search: window.location.search,
                              },
                            }),
                          })}
                        >
                          <td className="p-3">
                            {r.job_name ?? <span className="text-muted-foreground">ad-hoc</span>}
                          </td>
                          <td className="p-3"><StatusBadge status={r.status} /></td>
                          <td className="p-3 text-muted-foreground">{r.trigger}</td>
                          <td className="p-3 text-muted-foreground">{isoSeconds(r.started_at)}</td>
                          <td className="p-3 text-muted-foreground">{durationLabel(r.started_at, r.finished_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Pager — server-side LIMIT/OFFSET with COUNT(*) OVER for
                the total. Refilter / change of page size resets page=1
                via navWith. */}
            <div className="flex items-center justify-between text-sm flex-wrap gap-3">
              <span className="text-muted-foreground">
                {data
                  ? data.total === 0
                    ? "0 runs"
                    : `${offset + 1}–${Math.min(offset + data.runs.length, data.total)} of ${data.total}`
                  : ""}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground">Per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => navWith({ perPage: Number(v), page: 1 })}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PER_PAGE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNum <= 1}
                  onClick={() => navWith({ page: pageNum - 1 })}
                >
                  Prev
                </Button>
                <span className="text-muted-foreground">
                  Page <span className="font-mono">{pageNum}</span> of{" "}
                  <span className="font-mono">{totalPages}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNum >= totalPages}
                  onClick={() => navWith({ page: pageNum + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </PageContent>
  );
}
